import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { logger } from '@/lib/logging';
import { FRONTEND_UI_EVENTS } from '@blms/shared/telemetry/events';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

const DEFAULT_TRACE_TARGETS: Array<string | RegExp> = ['localhost', /^\/api/];

const clampSampleRate = (rawValue: string | undefined, fallback: number) => {
  if (rawValue === undefined || rawValue === '') {
    return fallback;
  }

  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.min(Math.max(parsed, 0), 1);
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Sentry matches string targets as substrings of the request URL, so a relative
// target such as `/api/v1` would also match `https://third-party.example/api/v1`.
// Anchor relative targets to the serving origin (and to relative request URLs)
// so trace headers never leave the app's own origin.
export const toTraceTarget = (entry: string, browserOrigin?: string): string | RegExp => {
  if (!entry.startsWith('/')) {
    return entry;
  }
  const origin = browserOrigin ? escapeRegExp(browserOrigin) : '';
  const path = entry.length > 1 && entry.endsWith('/') ? entry.slice(0, -1) : entry;
  return new RegExp(`^(?:${origin})?${escapeRegExp(path)}(?:[/?#]|$)`);
};

const buildTraceTargets = (): Array<string | RegExp> => {
  const rawTargets = import.meta.env.VITE_SENTRY_TRACE_TARGETS;
  if (!rawTargets) {
    return DEFAULT_TRACE_TARGETS;
  }

  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
  const entries = rawTargets
    .split(',')
    .map((value: string) => value.trim())
    .filter(Boolean)
    .map((value: string) => toTraceTarget(value, browserOrigin));

  return entries.length > 0 ? entries : DEFAULT_TRACE_TARGETS;
};

type ReleaseBuildInputs = {
  appVersion?: string;
  gitCommit?: string;
  gitCommitShort?: string;
  fallbackGitCommit?: string;
};

export const buildRelease = ({
  appVersion = import.meta.env.VITE_APP_VERSION,
  gitCommit = import.meta.env.VITE_GIT_COMMIT,
  gitCommitShort = import.meta.env.VITE_GIT_COMMIT_SHORT,
  fallbackGitCommit = typeof __GIT_COMMIT__ === 'string' ? __GIT_COMMIT__ : '',
}: ReleaseBuildInputs = {}) =>
  appVersion || gitCommit || gitCommitShort || fallbackGitCommit || 'dev';

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.DEV) {
      logger.warn('[telemetry] VITE_SENTRY_DSN missing, skipping Sentry bootstrap.', {
        event: FRONTEND_UI_EVENTS.loggerMissingDsn,
      });
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
    release: buildRelease(),
    sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === 'true',
    tracePropagationTargets: buildTraceTargets(),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: clampSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),
  });
};
